import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import Tooltip from './Tooltip';
import { showConfirm, showAlert } from '../utils/ModalService';


interface Property {
  id: string;
  address: string;
}

interface Deal {
  id: string;
  deal_name: string;
  deal_type?: string;
  status?: string;
  property_id?: string;
  target_price?: number;
  offer_price?: number;
  closing_date?: string;
  description?: string;
  source?: string;
}

export const DealSourcing: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [newDeal, setNewDeal] = useState<Partial<Deal>>({});
  const [message, setMessage] = useState('');
  const [editingDealId, setEditingDealId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchDeals(),
          fetchProperties()
        ]);
      } catch (err) {
        setError('Failed to fetch data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await api.get('/deals');
      setDeals(response.data);
    } catch (err: any) {
      console.error('Error fetching deals:', err.response?.data || err.message);
      throw err; // Re-throw to be caught by fetchData's catch block
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/properties');
      setProperties(response.data);
    } catch (err: any) {
      console.error('Error fetching properties:', err.response?.data || err.message);
      throw err; // Re-throw to be caught by fetchData's catch block
    }
  };

  if (loading) {
    return <div className="container mx-auto p-4">{t("dealSourcing.k1")}</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-danger">{t("dealSourcing.k2")} {error}</div>;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewDeal({ ...newDeal, [name]: value });
  };

  const handleAddDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    try {
      const dealToSend = { ...newDeal };
      if (dealToSend.property_id === '') {
        dealToSend.property_id = undefined; // Convert empty string to undefined for optional fields
      }
      const response = await api.post('/deals', dealToSend);
      setMessage('Deal added successfully!');
      setNewDeal({}); // Clear form
      // Add the newly created deal to the state and then re-fetch all deals to ensure proper sorting and data consistency
      if (response.data) {
        setDeals(prevDeals => [response.data, ...prevDeals]); // Add new deal to the beginning
      }
    } catch (error: any) {
      console.error('Error adding deal:', error.response?.data || error.message);
      setMessage(error.response?.data?.detail || 'An error occurred during deal creation.');
    }
  };

  const handleEditClick = (deal: Deal) => {
    setEditingDealId(deal.id);
    setNewDeal(deal); // Populate form with deal data for editing
  };

  const handleUpdateDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    if (!editingDealId) return;

    try {
      await api.put(`/deals/${editingDealId}`, newDeal);
      setMessage('Deal updated successfully!');
      setEditingDealId(null); // Exit editing mode
      setNewDeal({}); // Clear form
      fetchDeals(); // Refresh list
    } catch (error: any) {
      console.error('Error updating deal:', error.response?.data || error.message);
      setMessage(error.response?.data?.detail || 'An error occurred during deal update.');
    }
  };

  const handleDeleteDeal = async (dealId: string) => {
    setMessage('');
    showConfirm('Confirm Action', 'Are you sure you want to delete this deal?', async () => {
      try {
        await api.delete(`/deals/${dealId}`);
        setMessage('Deal deleted successfully!');
        fetchDeals(); // Refresh list
      } catch (error: any) {
        console.error('Error deleting deal:', error.response?.data || error.message);
        setMessage(error.response?.data?.detail || 'An error occurred during deal deletion.');
      }
    });
  };

  return (
    <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-secondary">{t("dealSourcing.k3")}</h2>
        <Tooltip text="This page allows you to track and manage your deal pipeline. You can add new deals, update their status, and link them to properties in your portfolio. This is your central hub for managing all your acquisition, disposition, and development deals." />
      </div>

      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{editingDealId ? t("dealSourcing.k4") : t("dealSourcing.k5")}</h3>
      <form onSubmit={editingDealId ? handleUpdateDeal : handleAddDeal} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
        <input type="text" name="deal_name" placeholder={t("dealSourcing.k6")} value={newDeal.deal_name || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <select name="deal_type" value={newDeal.deal_type || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full">
          <option value="">{t("dealSourcing.k7")}</option>
          <option value="acquisition">{t("dealSourcing.k8")}</option>
          <option value="disposition">{t("dealSourcing.k9")}</option>
          <option value="development">{t("dealSourcing.k10")}</option>
        </select>
        <select name="status" value={newDeal.status || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full">
          <option value="">{t("dealSourcing.k11")}</option>
          <option value="pipeline">{t("dealSourcing.k12")}</option>
          <option value="due_diligence">{t("dealSourcing.k13")}</option>
          <option value="closed">{t("dealSourcing.k14")}</option>
          <option value="lost">{t("dealSourcing.k15")}</option>
        </select>
        <select name="property_id" value={newDeal.property_id || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full">
          <option value="">{t("dealSourcing.k16")}</option>
          {properties.map(prop => (
            <option key={prop.id} value={prop.id}>{prop.address}</option>
          ))}
        </select>
        <input type="number" name="target_price" placeholder={t("dealSourcing.k17")} value={newDeal.target_price || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <input type="number" name="offer_price" placeholder={t("dealSourcing.k18")} value={newDeal.offer_price || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <div className="flex flex-col">
          <label htmlFor="closing_date" className="text-text-secondary">{t("dealSourcing.k19")}</label>
          <input type="date" id="closing_date" name="closing_date" value={newDeal.closing_date || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        </div>
        <input type="text" name="source" placeholder={t("dealSourcing.k20")} value={newDeal.source || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <textarea name="description" placeholder={t("dealSourcing.k21")} value={newDeal.description || ''} onChange={handleInputChange} className="col-span-full border border-border-primary p-2 rounded w-full"></textarea>
        <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
          {editingDealId ? t("dealSourcing.k22") : t("dealSourcing.k23")}
        </button>
        {editingDealId && (
          <button type="button" onClick={() => { setEditingDealId(null); setNewDeal({}); setMessage(''); }} className="col-span-full bg-transparent border border-border-primary text-text-primary px-4 py-2 rounded hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-primary">
            {t("dealSourcing.k24")} </button>
        )}
      </form>

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("dealSourcing.k25")}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {deals.length === 0 ? (
          <p className="text-text-secondary">{t("dealSourcing.k26")}</p>
        ) : (
          deals.map((deal) => (
            <div key={deal.id} className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
              <h4 className="text-lg font-semibold mb-2">{deal.deal_name} ({deal.deal_type})</h4>
              <p className="text-text-secondary"><strong>{t("dealSourcing.k27")}</strong> {deal.status}</p>
              {deal.property_id && <p className="text-text-secondary"><strong>{t("dealSourcing.k28")}</strong> {properties.find(p => p.id === deal.property_id)?.address || 'N/A'}</p>}
              {deal.target_price && <p className="text-text-secondary"><strong>{t("dealSourcing.k29")}</strong> ${deal.target_price.toLocaleString()}</p>}
              {deal.offer_price && <p className="text-text-secondary"><strong>{t("dealSourcing.k30")}</strong> ${deal.offer_price.toLocaleString()}</p>}
              {deal.closing_date && <p className="text-text-secondary"><strong>{t("dealSourcing.k19")}</strong> {new Date(deal.closing_date).toLocaleDateString('en-US')}</p>}
              {deal.source && <p className="text-text-secondary"><strong>{t("dealSourcing.k31")}</strong> {deal.source}</p>}
              {deal.description && <p className="text-text-secondary"><strong>{t("dealSourcing.k32")}</strong> {deal.description}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => handleEditClick(deal)} className="bg-success text-white px-4 py-2 rounded hover:bg-success-dark">{t("dealSourcing.k33")}</button>
                <button onClick={() => handleDeleteDeal(deal.id)} className="bg-danger text-white px-4 py-2 rounded hover:bg-red-700">{t("dealSourcing.k34")}</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

